import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';

import type { OpenRepairOrder, StateTree } from '@makemydeal/dr-dash-types';

import * as offerReduxSelectors from '../selectors/offerRedux';
import * as repairOrderSelectors from '../selectors/repairOrderSelectors';
import * as vehicleSelectors from '../selectors/vehicleSelectors';

// New Relic event names for the Open RO alert feature (F227301 / US2077492). The `MV:` prefix keeps
// these grouped with the rest of the Manager View telemetry in New Relic.
export const NR_REPAIR_ORDERS_ALERT_LOADED = 'MV:RepairOrders - Alert Loaded';
export const NR_REPAIR_ORDERS_DETAIL = 'MV:RepairOrders - Detail';
export const NR_REPAIR_ORDERS_DRAWER_OPENED = 'MV:RepairOrders - Drawer Opened';

// New Relic product/account identifier for Manager View events. US2077492 AC 5 requires the
// application name on every event.
const NR_APPLICATION_NAME = 'MV';

// The identifiers US2077492 AC 6 requires on every RO event so each metric can be correlated back to
// a specific deal, dealer, and vehicle. Kept in one place so the emitters below stay consistent.
const collectRepairOrderContext = (state: StateTree) => ({
    applicationName: NR_APPLICATION_NAME,
    correlationId: offerReduxSelectors.getSessionId(state),
    dealXgId: offerReduxSelectors.getDealXgId(state),
    dealXgVersion: offerReduxSelectors.getDealXgVersion(state),
    dealerId: dealerSelectors.getDealerId(state),
    vin: vehicleSelectors.getVehicleVin(state),
    vehicleCondition: vehicleSelectors.getVehicleCondition(state),
    vehicleYear: vehicleSelectors.getVehicleYear(state),
    vehicleMake: vehicleSelectors.getVehicleMake(state),
    vehicleModel: vehicleSelectors.getVehicleModel(state),
    vehicleTrim: vehicleSelectors.getVehicleTrim(state)
});

// A New Relic page action only accepts a flat attribute bag, so the RO's service names are collapsed
// into a single string rather than nested. Read from the raw model (not the dash-components display
// helpers) to keep dash-store free of a dash-components dependency / import cycle.
const serviceNamesOf = (repairOrder: OpenRepairOrder): string =>
    repairOrder.services
        .map((service) => service.description?.trim() || service.nameAggregate?.trim())
        .filter(Boolean)
        .join(', ');

/**
 * Emits the telemetry captured once a deal's repair-order data has loaded. Fires on every successful
 * fetch — including when there are none — so New Relic has the full denominator to chart the share of
 * deals with any open ROs, the open-RO count distribution, and open ROs by vehicle condition. Adds one
 * detail event per open RO to feed the per-dealer RO details table (US2077492 AC 3).
 */
export const logRepairOrdersLoadedEvent = (state: StateTree) => {
    const repairOrders = repairOrderSelectors.getRepairOrders(state);
    const context = collectRepairOrderContext(state);

    logNewRelicPageAction(NR_REPAIR_ORDERS_ALERT_LOADED, {
        ...context,
        openRepairOrderCount: repairOrders.length,
        hasOpenRepairOrders: repairOrders.length > 0
    });

    repairOrders.forEach((repairOrder) => {
        logNewRelicPageAction(NR_REPAIR_ORDERS_DETAIL, {
            ...context,
            openRepairOrderCount: repairOrders.length,
            orderNumber: repairOrder.orderNumber,
            total: repairOrder.total,
            lastUpdatedDate: repairOrder.lastUpdatedDate,
            serviceNames: serviceNamesOf(repairOrder)
        });
    });
};

/**
 * Emits the event captured when the user opens the RO details drawer. Counted against the
 * `Alert Loaded` events that had open ROs to derive the drawer opened-vs-ignored split (US2077492 AC 3).
 */
export const logRepairOrdersDrawerOpenedEvent = (state: StateTree) => {
    logNewRelicPageAction(NR_REPAIR_ORDERS_DRAWER_OPENED, {
        ...collectRepairOrderContext(state),
        openRepairOrderCount: repairOrderSelectors.getRepairOrderAlertCount(state)
    });
};
